
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase, genericSupabase } from "@/integrations/supabase/client";
import { useToast } from '@/components/ui/use-toast';
import { sendSMS } from '@/utils/smsUtils';
import { sendEmailNotification } from '@/utils/emailUtils';
import { calculateDistance } from '@/utils/geoUtils';

// Define and export the type definitions
export type ServiceType = "εξοικονομώ" | "αντλίεςΘερμότητας" | "φωτοβολταϊκά" | "κλιματιστικά" | "φυσικόΑέριο" | "ηλιακά" | "άλλο";
export type LeadStageType = "σεΕκκρεμότητα" | "έκλεισεΗΔουλειά" | "ήθελεΠληροφορίες" | "Νέο";

export interface Lead {
  id: string;
  name: string;
  phone: string;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  comments?: string;
  stage: LeadStageType;
  partnerId: string | null;
  partnerName: string | null;
  createdAt: string;
  updatedAt: string;
  smsStatus: "sent" | "delivered" | "failed" | null;
  viewed: boolean;
  viewedAt: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface LeadContextType {
  leads: Lead[];
  addLead: (name: string, phone: string, address: string, postalCode: string, service: ServiceType, timeframe: string, comments?: string, latitude?: number | null, longitude?: number | null) => Promise<Lead>;
  getLeadById: (id: string) => Lead | undefined;
  updateLeadStage: (id: string, stage: LeadStageType) => void;
  markLeadAsViewed: (id: string) => void;
  sendSMS: (leadId: string, message: string, phone: string, partnerId?: string) => Promise<void>;
  sendEmail: (leadId: string, email: string, subject: string, message: string, partnerId?: string) => Promise<void>;
  refreshLeads: () => Promise<void>;
  assignLeadToPartner: (leadId: string, partnerId: string, notifyPartner?: boolean) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
}

const LeadContext = createContext<LeadContextType | undefined>(undefined);

export const LeadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const { toast } = useToast();

  const refreshLeads = useCallback(async () => {
    try {
      // This will work with RLS because we're including the Admin-Id header via the supabase client
      const { data, error } = await supabase
        .from('leads')
        .select('*');

      if (error) {
        console.error("Error fetching leads:", error);
        return;
      }

      const leadsWithPartnerName = await Promise.all(
        data.map(async (lead) => {
          if (lead.partner_id) {
            const { data: partnerData, error: partnerError } = await supabase
              .from('partners')
              .select('name')
              .eq('id', lead.partner_id)
              .single();

            if (partnerError) {
              console.error("Error fetching partner name:", partnerError);
              return {
                ...lead,
                partnerName: null,
              };
            }

            return {
              ...lead,
              partnerName: partnerData?.name || null,
            };
          } else {
            return {
              ...lead,
              partnerName: null,
            };
          }
        })
      );

      const formattedLeads: Lead[] = leadsWithPartnerName.map(lead => ({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        address: lead.address,
        postalCode: lead.postal_code,
        service: lead.service as ServiceType,
        timeframe: lead.timeframe,
        comments: lead.comments || undefined,
        stage: (lead.stage || 'Νέο') as LeadStageType,
        partnerId: lead.partner_id || null,
        partnerName: lead.partnerName || null,
        createdAt: lead.created_at,
        updatedAt: lead.updated_at,
        smsStatus: lead.sms_sent === null ? null : (lead.sms_sent ? 'sent' : 'failed'),
        viewed: lead.link_opened || false,
        viewedAt: null,
        latitude: lead.latitude || null,
        longitude: lead.longitude || null,
      }));

      setLeads(formattedLeads);
    } catch (error) {
      console.error("Unexpected error fetching leads:", error);
    }
  }, []);

  useEffect(() => {
    refreshLeads();
  }, [refreshLeads]);

  const addLead = async (name: string, phone: string, address: string, postalCode: string, service: ServiceType, timeframe: string, comments?: string, latitude?: number | null, longitude?: number | null) => {
    const newLead: Lead = {
      id: uuidv4(),
      name,
      phone,
      address,
      postalCode,
      service,
      timeframe,
      comments,
      stage: 'Νέο' as LeadStageType,
      partnerId: null,
      partnerName: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      smsStatus: null,
      viewed: false,
      viewedAt: null,
      latitude: latitude || null,
      longitude: longitude || null,
    };

    try {
      const { error } = await supabase
        .from('leads')
        .insert({
          id: newLead.id,
          name: newLead.name,
          phone: newLead.phone,
          address: newLead.address,
          postal_code: newLead.postalCode,
          service: newLead.service,
          timeframe: newLead.timeframe,
          comments: newLead.comments,
          latitude: newLead.latitude,
          longitude: newLead.longitude,
        });

      if (error) {
        console.error("Error adding lead:", error);
        return newLead;
      }

      setLeads(prevLeads => [...prevLeads, newLead]);
      
      // If we have coordinates, auto-assign to the nearest partner based on strict geographical proximity
      if (latitude && longitude) {
        try {
          // Get all active partners that offer this service
          const { data: eligiblePartners, error: partnersError } = await supabase
            .from('partners')
            .select('*')
            .eq('active', true)
            .contains('services', [service]);
            
          if (partnersError) {
            console.error("Error fetching eligible partners:", partnersError);
          } else if (eligiblePartners && eligiblePartners.length > 0) {
            // Filter to only partners with location data and radius
            const partnersWithLocation = eligiblePartners.filter(
              p => p.latitude && p.longitude && p.radius
            );
            
            if (partnersWithLocation.length > 0) {
              // Find partners within their specified radius
              const partnersInRange = partnersWithLocation.filter(partner => {
                const distance = calculateDistance(
                  latitude, 
                  longitude, 
                  partner.latitude!, 
                  partner.longitude!
                );
                console.info(`Partner ${partner.name} is ${distance.toFixed(2)}km away (radius: ${partner.radius}km)`);
                return distance <= partner.radius!;
              });
              
              if (partnersInRange.length > 0) {
                console.info(`Found ${partnersInRange.length} partners in range for lead ${newLead.id}`);
                
                // Sort by distance (closest first)
                partnersInRange.sort((a, b) => {
                  const distA = calculateDistance(latitude, longitude, a.latitude!, a.longitude!);
                  const distB = calculateDistance(latitude, longitude, b.latitude!, b.longitude!);
                  return distA - distB;
                });
                
                // Assign to the closest partner
                const closestPartner = partnersInRange[0];
                console.info(`Successfully assigned partner ${closestPartner.name} to lead ${newLead.id} as closest match (distance: ${calculateDistance(latitude, longitude, closestPartner.latitude!, closestPartner.longitude!).toFixed(2)}km)`);
                
                await assignLeadToPartner(newLead.id, closestPartner.id, true);
              } else {
                console.info(`No partners in range for lead ${newLead.id}`);
                // Auto-notify admin about unassigned lead
                try {
                  const { data: adminData } = await supabase
                    .from('administrators')
                    .select('email')
                    .eq('active', true)
                    .limit(1);
                  if (adminData && adminData.length > 0 && adminData[0].email) {
                    const leadDetails = {
                      name: newLead.name,
                      service: newLead.service,
                      phone: newLead.phone,
                      address: newLead.address,
                      timeframe: newLead.timeframe,
                      comments: newLead.comments
                    };
                    await sendEmailNotification(
                      adminData[0].email,
                      newLead.id,
                      leadDetails,
                      undefined,
                      'admin_unassigned'
                    );
                    console.info(`Admin notified about unassigned lead ${newLead.id}`);
                  }
                } catch (adminNotifyErr) {
                  console.error('Failed to notify admin about unassigned lead:', adminNotifyErr);
                }
              }
            }
          }
        } catch (error) {
          console.error("Error auto-assigning lead:", error);
        }
      }
    } catch (error) {
      console.error("Unexpected error adding lead:", error);
    }

    return newLead;
  };

  const getLeadById = (id: string): Lead | undefined => {
    return leads.find(lead => lead.id === id);
  };

  const updateLeadStage = async (id: string, stage: LeadStageType) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ stage })
        .eq('id', id);

      if (error) {
        console.error("Error updating lead stage:", error);
        return;
      }

      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === id ? { ...lead, stage, updatedAt: new Date().toISOString() } : lead
        )
      );
    } catch (error) {
      console.error("Unexpected error updating lead stage:", error);
    }
  };

  const markLeadAsViewed = async (id: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ link_opened: true })
        .eq('id', id);

      if (error) {
        console.error("Error marking lead as viewed:", error);
        return;
      }

      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === id ? { ...lead, viewed: true, viewedAt: new Date().toISOString() } : lead
        )
      );
    } catch (error) {
      console.error("Unexpected error marking lead as viewed:", error);
    }
  };

  const sendSMS = async (leadId: string, message: string, phone: string, partnerId?: string) => {
    try {
      const response = await fetch('https://leads.lineweb.gr/api/send-sms.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phone,
          message: message,
          leadId: leadId,
          partnerId: partnerId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'sent') {
        await supabase
          .from('leads')
          .update({ sms_sent: true })
          .eq('id', leadId);
          
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? { ...lead, smsStatus: 'sent' as const, updatedAt: new Date().toISOString() } : lead
          )
        );

        // If we have a partner ID, log this in Supabase
        if (partnerId) {
          try {
            await genericSupabase
              .from('sms_logs')
              .insert({
                lead_id: leadId,
                partner_id: partnerId,
                status: 'sent',
                message_id: data.messageId || null
              });
          } catch (logError) {
            console.error("Error logging SMS in Supabase:", logError);
          }
        }

        toast({
          title: "SMS εστάλη επιτυχώς",
          description: "Το μήνυμα SMS εστάλη με επιτυχία.",
          variant: "default",
        });
      } else {
        await supabase
          .from('leads')
          .update({ sms_sent: false })
          .eq('id', leadId);
          
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? { ...lead, smsStatus: 'failed' as const, updatedAt: new Date().toISOString() } : lead
          )
        );

        // Log failed SMS
        if (partnerId) {
          try {
            await genericSupabase
              .from('sms_logs')
              .insert({
                lead_id: leadId,
                partner_id: partnerId,
                status: 'failed'
              });
          } catch (logError) {
            console.error("Error logging failed SMS in Supabase:", logError);
          }
        }

        console.error("SMS sending failed:", data.error);
        toast({
          title: "Αποτυχία αποστολής SMS",
          description: data.error || "Υπήρξε πρόβλημα κατά την αποστολή του SMS.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId ? { ...lead, smsStatus: 'failed' as const, updatedAt: new Date().toISOString() } : lead
        )
      );
      toast({
        title: "Αποτυχία αποστολής SMS",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την αποστολή του SMS.",
        variant: "destructive",
      });
    }
  };

  const sendEmail = async (leadId: string, email: string, subject: string, message: string, partnerId?: string) => {
    try {
      const lead = leads.find(lead => lead.id === leadId);
      
      if (!lead) {
        throw new Error("Lead not found");
      }
      
      const leadDetails = {
        name: lead.name,
        service: lead.service,
        phone: lead.phone,
        address: lead.address,
        timeframe: lead.timeframe,
        comments: lead.comments
      };

      const result = await sendEmailNotification(email, leadId, leadDetails, partnerId);
      
      if (result.status === 'success') {
        toast({
          title: "Email εστάλη",
          description: "Το email εστάλη με επιτυχία.",
          variant: "default",
        });
      } else {
        toast({
          title: "Αποτυχία αποστολής email",
          description: result.error || "Υπήρξε πρόβλημα κατά την αποστολή του email.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Αποτυχία αποστολής email",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την αποστολή του email.",
        variant: "destructive",
      });
    }
  };

  const assignLeadToPartner = async (leadId: string, partnerId: string, notifyPartner: boolean = false) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ partner_id: partnerId })
        .eq('id', leadId);

      if (error) {
        console.error("Error assigning lead to partner:", error);
        throw new Error(error.message);
      }

      // Get partner name and contact info
      const { data: partnerData, error: partnerError } = await supabase
        .from('partners')
        .select('name, phone, email')
        .eq('id', partnerId)
        .single();

      if (partnerError) {
        console.error("Error fetching partner name:", partnerError);
        throw new Error(partnerError.message);
      }

      // Update local state
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId
            ? {
                ...lead,
                partnerId,
                partnerName: partnerData?.name || null,
                updatedAt: new Date().toISOString()
              }
            : lead
        )
      );

      // Notify the partner if requested
      if (notifyPartner && partnerData) {
        const lead = leads.find(lead => lead.id === leadId);
        if (lead) {
          // Send SMS if we have a phone number
          if (partnerData.phone) {
            const smsMessage = `Νέος ενδιαφερόμενος: ${lead.name}, ${lead.service}. Δείτε περισσότερα: https://leads.lineweb.gr/lead/${lead.id}`;
            await sendSMS(leadId, smsMessage, partnerData.phone, partnerId);
          }
          
          // Send email if we have an email address
          if (partnerData.email) {
            await sendEmail(leadId, partnerData.email, "Νέος Ενδιαφερόμενος", "", partnerId);
          }
        }
      }

      toast({
        title: "Επιτυχής ανάθεση",
        description: `Ο ενδιαφερόμενος ανατέθηκε στον συνεργάτη ${partnerData?.name}`,
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to assign lead to partner:", error);
      toast({
        title: "Αποτυχία ανάθεσης",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την ανάθεση του ενδιαφερόμενου.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteLead = async (leadId: string): Promise<void> => {
    try {
      // First delete related SMS delivery status records
      // This will work with RLS because we're including the Admin-Id header via the genericSupabase client
      const { error: deliveryStatusError } = await genericSupabase
        .from('sms_delivery_status')
        .delete()
        .eq('lead_id', leadId);

      if (deliveryStatusError) {
        console.error("Error deleting related SMS delivery status:", deliveryStatusError);
        // Continue with deletion attempt even if this fails
      }

      // Delete related SMS logs records
      const { error: smsLogsError } = await genericSupabase
        .from('sms_logs')
        .delete()
        .eq('lead_id', leadId);

      if (smsLogsError) {
        console.error("Error deleting related SMS logs:", smsLogsError);
        // Continue with deletion attempt even if this fails
      }

      // Finally delete the lead record
      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) {
        console.error("Error deleting lead:", error);
        throw new Error(error.message);
      }

      // Remove from local state
      setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
    } catch (error) {
      console.error("Failed to delete lead:", error);
      throw error;
    }
  };

  const value: LeadContextType = {
    leads,
    addLead,
    getLeadById,
    updateLeadStage,
    markLeadAsViewed,
    sendSMS,
    sendEmail,
    refreshLeads,
    assignLeadToPartner,
    deleteLead,
  };

  return (
    <LeadContext.Provider value={value}>
      {children}
    </LeadContext.Provider>
  );
};

export const useLeads = (): LeadContextType => {
  const context = useContext(LeadContext);
  if (context === undefined) {
    throw new Error("useLeads must be used within a LeadProvider");
  }
  return context;
};
